import { useSuspenseQuery } from "@tanstack/react-query";
import { CircleCheck, Landmark } from "lucide-react";
import { Suspense, useEffect } from "react";

import { orpc } from "@/client";
import { ErrorBoundary } from "@/components/error-boundary";
import { cn } from "@/lib/utils";

import { FinanceListSkeleton, ReconciliationSummarySkeleton } from "./area-skeleton";
import { ReconciliationSettlements } from "./reconciliation-settlements";
import { ReconciliationTimeline } from "./reconciliation-timeline";
import type { FinanceModal } from "./types";

interface ReconciliationAreaProps {
	month: string;
	page: number;
	onPage: (page: number) => void;
	onModal: (modal: FinanceModal) => void;
}

export function ReconciliationArea(props: ReconciliationAreaProps) {
	return (
		<>
			<ReconciliationSettlements month={props.month} onModal={props.onModal} />
			<ErrorBoundary key={props.month}>
				<Suspense fallback={<ReconciliationSummarySkeleton />}>
					<ReconciliationSummary month={props.month} />
				</Suspense>
			</ErrorBoundary>
			<ErrorBoundary key={`${props.month}:${props.page}`}>
				<Suspense fallback={<FinanceListSkeleton />}>
					<ReconciliationList {...props} />
				</Suspense>
			</ErrorBoundary>
		</>
	);
}

function ReconciliationSummary({ month }: { month: string }) {
	const { data } = useSuspenseQuery(
		orpc.financeiro.reconciliation.summary.queryOptions({ input: { month } }),
	);
	let title = `${data.total} itens em aberto`;
	let detail = "Recebimentos, repasses e movimentos que ainda exigem revisão.";
	let Icon = Landmark;
	let resolved = false;

	if (data.total === 1) {
		title = "1 item em aberto";
	}

	if (data.total === 0) {
		title = "Tudo conciliado";
		detail = "Não há decisões financeiras pendentes neste mês.";
		Icon = CircleCheck;
		resolved = true;
	}

	return (
		<div
			className={cn(
				"mt-5 flex items-center justify-between rounded-lg border px-4 py-3",
				resolved && "border-info/25 bg-info/10",
				!resolved && "border-warning/25 bg-warning-soft",
			)}
		>
			<div>
				<p className="font-display text-lg font-semibold text-foreground">{title}</p>
				<p className="text-xs text-muted-foreground">{detail}</p>
			</div>
			<Icon className={cn("size-6", resolved && "text-info", !resolved && "text-warning")} />
		</div>
	);
}

function ReconciliationList({ month, page, onPage, onModal }: ReconciliationAreaProps) {
	const { data } = useSuspenseQuery(
		orpc.financeiro.reconciliation.list.queryOptions({ input: { month, page } }),
	);

	useEffect(() => {
		if (page > data.pages) {
			onPage(data.pages);
		}
	}, [data.pages, onPage, page]);

	if (data.data.length === 0) {
		return null;
	}

	return (
		<section>
			<div className="mb-2 mt-5">
				<h2 className="type-section-title text-foreground">Outros itens</h2>
				<p className="text-xs text-muted-foreground">
					Recebimentos, contas, banco e vendas que não pertencem a um repasse.
				</p>
			</div>
			<ReconciliationTimeline
				items={data.data}
				page={page}
				pages={data.pages}
				onPage={onPage}
				onModal={onModal}
			/>
		</section>
	);
}
